import { z } from 'zod';

/**
 * Request/response schemas for the email-OTP auth flow and session bootstrap
 * (ARCHITECTURE_PLAN §6.1, §9). Single source of truth shared by the Worker
 * (validators) and the SPA (typed client) per §3.
 *
 * Route handlers are implemented in a later Phase 1 slice; these schemas are
 * the contract they and the SPA are built against.
 */

/** Email addresses are normalized (trim + lowercase) before validation/storage. */
export const EmailSchema = z.string().trim().toLowerCase().email();

/**
 * `POST /api/auth/request-code` body (§6.1 step 1). The Turnstile token is
 * verified server-side before any auth-code row is created.
 */
export const RequestCodeBodySchema = z.object({
  email: EmailSchema,
  turnstileToken: z.string().min(1),
});
export type RequestCodeBody = z.infer<typeof RequestCodeBodySchema>;

/**
 * `POST /api/auth/verify-code` body (§6.1 step 2). Codes are exactly 6 digits,
 * generated without modulo bias (see `src/auth/crypto.ts`).
 */
export const VerifyCodeBodySchema = z.object({
  email: EmailSchema,
  code: z.string().regex(/^\d{6}$/, 'code must be 6 digits'),
});
export type VerifyCodeBody = z.infer<typeof VerifyCodeBodySchema>;

/** The authenticated user as exposed to the SPA (no internal flags beyond §6). */
export const SessionUserSchema = z.object({
  id: z.string(),
  email: z.string(),
  name: z.string(),
  isPlatformAdmin: z.boolean(),
});
export type SessionUser = z.infer<typeof SessionUserSchema>;

/** One workspace membership of the session user (drives workspace switcher). */
export const MembershipSummarySchema = z.object({
  tenantId: z.string(),
  slug: z.string(),
  name: z.string(),
  role: z.enum(['owner', 'admin', 'staff']),
});
export type MembershipSummary = z.infer<typeof MembershipSummarySchema>;

/**
 * Session state exposed to the SPA. `isImpersonating` is true when the
 * session carries an `impersonation_grant_id` (§6.4); `impersonationGrantId`
 * is the grant id (so the SPA can revoke), and `impersonatingTenantSlug` is
 * the tenant the grant scopes into (so the SPA renders the banner + the
 * admin console knows where the impersonation is rooted).
 */
export const SessionStateSchema = z.object({
  isImpersonating: z.boolean(),
  impersonationGrantId: z.string().nullable(),
  impersonatingTenantId: z.string().nullable(),
  impersonatingTenantSlug: z.string().nullable(),
});
export type SessionState = z.infer<typeof SessionStateSchema>;

/**
 * `GET /api/auth/me` response. `user` is `null` (with an empty memberships
 * list) when there is no valid session — the SPA uses this for route guards
 * without treating a logged-out visitor as an error (§10).
 *
 * `POST /api/auth/verify-code` returns this same shape on success (the
 * freshly authenticated user + memberships), so the SPA bootstraps from one
 * contract either way.
 *
 * `session` reports the impersonation state (§6.4) so the SPA can render the
 * banner and route guards can refuse admin-console access to an
 * impersonating session.
 */
export const MeResponseSchema = z.object({
  user: SessionUserSchema.nullable(),
  memberships: z.array(MembershipSummarySchema),
  session: SessionStateSchema,
});
export type MeResponse = z.infer<typeof MeResponseSchema>;

/**
 * `POST /api/auth/request-code` response (§6.1). IDENTICAL whether or not the
 * email exists (no user enumeration): `accepted` means only "the request was
 * well-formed and accepted for processing", never "an email was sent".
 */
export const RequestCodeResponseSchema = z.object({
  accepted: z.literal(true),
});
export type RequestCodeResponse = z.infer<typeof RequestCodeResponseSchema>;

/** `POST /api/auth/logout` response. Logout is idempotent (always 200). */
export const LogoutResponseSchema = z.object({
  loggedOut: z.literal(true),
});
export type LogoutResponse = z.infer<typeof LogoutResponseSchema>;

/**
 * Platform-admin password login (migration 0006 `user_credentials`) — a
 * password is a super-user capability alongside the email OTP flow, so the
 * admin console stays reachable even when the email provider is down.
 */

/** Password policy for platform-admin credentials (length is the primary control, NIST 800-63B). */
export const PasswordSchema = z
  .string()
  .min(12, 'password must be at least 12 characters')
  .max(128, 'password must be at most 128 characters');

/**
 * `GET /api/auth/password/status` — first-run bootstrap probe. `setupRequired`
 * is true ONLY while no password credential exists at all; the SPA routes the
 * operator to `/admin/setup` exactly once.
 */
export const PasswordStatusResponseSchema = z.object({
  setupRequired: z.boolean(),
});
export type PasswordStatusResponse = z.infer<typeof PasswordStatusResponseSchema>;

/**
 * `POST /api/auth/password/setup` body — one-time first-run claim: allowed
 * only while NO credential row exists, and only for an already-seeded
 * platform-admin user. Turnstile-gated like every public auth POST.
 */
export const PasswordSetupBodySchema = z.object({
  email: EmailSchema,
  password: PasswordSchema,
  turnstileToken: z.string().min(1),
});
export type PasswordSetupBody = z.infer<typeof PasswordSetupBodySchema>;

/** `POST /api/auth/password/login` body. Response is the shared `MeResponse`. */
export const PasswordLoginBodySchema = z.object({
  email: EmailSchema,
  password: z.string().min(1),
  turnstileToken: z.string().min(1),
});
export type PasswordLoginBody = z.infer<typeof PasswordLoginBodySchema>;
